import { useState } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import type { TourKind } from 'components/marketplace/helpers/propertyActionability';
import { AVAILABLE_SOON_DISCLOSURE } from 'components/marketplace/shared/availableSoon';
import { useTouring } from 'components/self-tour/hooks/useTouring';
import type { ChosenSlot } from 'components/tours/TourBookingSteps';

import { BuyDirectContextModal } from '../PropertySection/BuyDirectContextModal';
import {
  SelfTourDialogSurface,
  selfTourHome,
  SelfTourQueryClientProvider,
} from '../PropertySection/SelfTourDialog';
import type { MarketplaceProperty } from '../types';
import { PdpAgentTourCard } from './PdpAgentTourCard';
import { PdpPurchaseCard } from './PdpPurchaseCard';
import { PdpTourCard } from './PdpTourCard';

export const PdpPriceCard = ({
  availableSoonCaptureRegion,
  property,
  onCopyLink,
  tourKindOverride,
  interactive = true,
}: {
  /** Coming-soon email capture, rendered inside the card below the disclosure. */
  availableSoonCaptureRegion?: React.ReactNode;
  property: MarketplaceProperty;
  onCopyLink: () => void;
  tourKindOverride?: TourKind;
  interactive?: boolean;
}) => {
  const touring = useTouring(property, { placement: 'main' });
  const tourKind = tourKindOverride ?? touring?.tourKind;
  const [initialTourSlot, setInitialTourSlot] = useState<ChosenSlot>();
  const openTour = (slot?: ChosenSlot) => {
    setInitialTourSlot(slot);
    (touring?.secondaryAction ?? touring?.primaryAction)?.onClick?.();
  };
  const buyDirectTouring =
    property.listingState === 'ON_THE_MARKET' && touring?.showBuyDirect ? touring : null;
  const hasMortgageRate = Boolean(property.mortgageOffer?.products.length);
  const showAgentTourCard = tourKind === 'tourWithAgent';
  const price = getDisplayPrice(property, 'listed');
  let priceLabel = 'Price not available';
  if (price != null) {
    priceLabel = formatCurrency(price);
  } else if (property.listingState === 'AVAILABLE_SOON') {
    priceLabel = 'Price pending';
  }

  return (
    <SelfTourQueryClientProvider>
      <Col className="w-full gap-5">
        {showAgentTourCard && <PdpAgentTourCard onCopyLink={onCopyLink} />}
        {touring && tourKind === 'selfTour' && (
          <PdpTourCard listingId={property.tourBookingListingId} onChoose={openTour} />
        )}
        {buyDirectTouring || hasMortgageRate ? (
          <PdpPurchaseCard
            key={`${property.addressToken}:${property.mortgageOffer?.quoteId}`}
            property={property}
            touring={buyDirectTouring}
          />
        ) : (
          <Col
            as="section"
            aria-label="Home price"
            className="@container w-full gap-6 rounded-2xl bg-od-bg-primary p-6 ring-1 ring-od-border-primary shadow-lg shadow-od-bg-scrim/5"
          >
            <Col className="w-full gap-2.5">
              {property.listingState === 'ON_THE_MARKET' && (
                <Text className="flex h-5 items-center text-body-sm text-od-content-secondary">
                  List price
                </Text>
              )}
              <Text
                as="h2"
                className="whitespace-nowrap text-subheader-sm @[18rem]:text-header-sm text-od-content-primary"
              >
                {priceLabel}
              </Text>
            </Col>
            {property.listingState === 'AVAILABLE_SOON' && (
              <Text as="p" className="text-body-sm text-od-content-tertiary">
                {AVAILABLE_SOON_DISCLOSURE}
              </Text>
            )}
            {property.listingState === 'AVAILABLE_SOON' && availableSoonCaptureRegion}
          </Col>
        )}
      </Col>
      {touring && interactive && (
        <>
          <SelfTourDialogSurface
            initialSlot={initialTourSlot}
            isOpen={touring.isSelfTourDialogOpen}
            onClose={touring.closeSelfTourDialog}
            onOpenRequest={touring.openSelfTourDialog}
            placement="main"
            addressToken={property.addressToken}
            home={selfTourHome(property)}
            slug={property.slug}
            tourBookingListingId={property.tourBookingListingId}
          />
          <BuyDirectContextModal
            isOpen={touring.isBuyDirectContextOpen}
            onClose={touring.closeBuyDirectContext}
            continueHref={touring.buyDirectContextHref}
          />
        </>
      )}
    </SelfTourQueryClientProvider>
  );
};
